import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./OrderBookPage.css";

const DEFAULT_PAGE_LIMIT = 50;
const EXPORT_BATCH = 500;
const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];
const EXCEL_SHEET_TITLE = "Order Book";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const getPairDisplay = (row) => {
  const askCurrency = row.ask_currency || row.base_currency_id;
  const payCurrency = row.pay_currency || row.quote_currency_id;
  const isBuy = (row.side || "").toUpperCase() === "BUY";
  return isBuy ? `${askCurrency}/${payCurrency}` : `${payCurrency}/${askCurrency}`;
};

const rowToExport = (row) => ({
  "Order ID": row.orderId || row._id || "",
  "Binance ID": row.binance_order_id ?? "",
  Created: row.createdAt ?? "",
  "User ID": row.user?.uuid || row.user?.id || row.user_id || "",
  "User Email": row.user?.email || row.user_email || "",
  "User Phone": row.user?.phone || row.phone || "",
  Pair: getPairDisplay(row),
  Side: row.side ?? "",
  "Order Type": row.order_type ?? "",
  Price: row.price ?? "",
  Quantity: row.quantity ?? "",
  Filled: row.filled ?? "",
  Remaining: row.remaining ?? "",
  "Total Fee": row.total_fee ?? "",
  Status: row.status ?? "",
});

const OrderBookPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(DEFAULT_PAGE_LIMIT);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, pageLimit]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 1000);
    return () => clearTimeout(handle);
  }, [search]);

  const buildParams = useCallback(
    (pageNum = page, limitNum = pageLimit) => {
      const params = { page: pageNum, limit: limitNum, status: statusFilter };
      if (debouncedSearch) params.user = debouncedSearch;
      return params;
    },
    [page, pageLimit, debouncedSearch, statusFilter]
  );

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await AdminService.getGlobalSpotOrders(buildParams());
      if (res?.success && Array.isArray(res.data)) {
        setOrders(res.data);
        setTotalPages(res.pagination?.total_pages || res.pagination?.totalPages || 1);
        setTotalCount(res.pagination?.total ?? res.data.length);
      } else {
        setOrders([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error("Failed to fetch global spot orders", err);
      setError("Failed to load orders from server.");
      setOrders([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const fetchAllFilteredOrders = async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];

    do {
      const res = await AdminService.getGlobalSpotOrders(buildParams(currentPage, EXPORT_BATCH));
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch data for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.totalPages || res?.pagination?.total_pages || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);

    return all;
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const allRows = await fetchAllFilteredOrders();
      if (!allRows.length) {
        setToast({ type: "error", message: "No orders match your filters to export." });
        setTimeout(() => setToast(null), 3000);
        return;
      }

      const exportData = allRows.map(rowToExport);
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, EXCEL_SHEET_TITLE);
      const statusLabel = statusFilter === "all" ? "all" : statusFilter.toLowerCase().replace(/\s+/g, "_");
      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `orderbook_${statusLabel}_${dateStamp}.xlsx`);

      setToast({ type: "success", message: `Exported ${allRows.length} order(s) to Excel.` });
      setTimeout(() => setToast(null), 4000);
    } catch (e) {
      console.error(e);
      setToast({ type: "error", message: e?.message || "Failed to export Excel file." });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setExportLoading(false);
    }
  };

  const handlePageLimitChange = (e) => {
    setPageLimit(Number(e.target.value) || DEFAULT_PAGE_LIMIT);
    setPage(1);
  };

  const openCount = orders.filter(
    (o) =>
      o.status === "PENDING" ||
      o.status === "PARTIAL" ||
      o.status === "PARTIALLY EXECUTED"
  ).length;

  const pageStart = orders.length === 0 ? 0 : (page - 1) * pageLimit + 1;
  const pageEnd = orders.length === 0 ? 0 : (page - 1) * pageLimit + orders.length;

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>OrderBook</span>
          </p>
          <h1 className="dashboard-hero__title">OrderBook</h1>
          <p className="dashboard-hero__subtitle">
            Live order book and open orders
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-book" />
        </div>
      </section>

      {toast && (
        <div className={`orderbook-toast orderbook-toast--${toast.type}`} role="status">
          <i className={`fas fa-${toast.type === "success" ? "check-circle" : "exclamation-circle"}`} />
          {toast.message}
        </div>
      )}

      <div className="dashboard-body">
        <section className="orderbook-section">
          <div className="orderbook-section__header">
            <div className="orderbook-section__title-wrap">
              <span className="orderbook-section__icon">
                <i className="fas fa-book-open" />
              </span>
              <h2 className="orderbook-section__title">Orders</h2>
            </div>
            <div className="orderbook-section__stats orderbook-section__stats--actions">
              <span className="orderbook-stat">
                <strong>{orders.length}</strong> on page
                {totalCount > 0 && (
                  <>
                    {" "}
                    (<strong>{totalCount}</strong> total)
                  </>
                )}
              </span>
              <span className="orderbook-stat">
                <strong>{openCount}</strong> Open
              </span>
              {totalPages > 1 && (
                <span className="orderbook-stat">
                  Page <strong>{page}</strong> / <strong>{totalPages}</strong>
                </span>
              )}
              <button
                type="button"
                className="orderbook-export-btn"
                onClick={handleExportExcel}
                disabled={exportLoading || loading}
                title="Download filtered orders as Excel"
              >
                <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
                {exportLoading ? "Exporting…" : "Download Excel"}
              </button>
              <button
                type="button"
                className="orderbook-refresh-btn"
                onClick={fetchOrders}
                disabled={loading}
                title="Refresh"
              >
                <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>
          {error && (
            <p className="orderbook-error" style={{ color: "#dc2626", marginTop: "0.5rem" }}>
              {error}
            </p>
          )}
          <div className="orderbook-search-wrap">
            <i className="fas fa-search" />
            <input
              type="text"
              placeholder="Search by order ID, email or pair..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="orderbook-search-input"
            />
            <select
              className="orderbook-search-input orderbook-status-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="PARTIALLY EXECUTED">Partially Executed</option>
              <option value="FILLED">Filled</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
            <label className="orderbook-page-limit">
              Rows per page
              <select
                value={pageLimit}
                onChange={handlePageLimitChange}
                className="orderbook-search-input orderbook-page-limit__select"
              >
                {PAGE_LIMIT_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="orderbook-table-wrap">
            <table className="orderbook-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Binance ID</th>
                  <th>Created</th>
                  <th>User ID</th>
                  <th>User Email</th>
                  <th>User Phone</th>
                  <th>Pair</th>
                  <th>Side</th>
                  <th>Order Type</th>
                  <th>Price</th>
                  <th>Quantity</th>
                  <th>Filled</th>
                  <th>Remaining</th>
                  <th>Total Fee</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && orders.length === 0 ? (
                  <tr>
                    <td colSpan={15} className="orderbook-empty">
                      <i className="fas fa-spinner fa-spin" /> Loading orders…
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={15} className="orderbook-empty">
                      No orders match your search.
                    </td>
                  </tr>
                ) : (
                  orders.map((row) => {
                    const orderId = row.orderId || row._id;
                    const binanceOrderId = row.binance_order_id || "---";
                    const traderId = row.user?.id || row.user_id;
                    const userId = row.user?.uuid || row.user?.id || row.user_id;
                    const userEmail = row.user?.email || row.user_email || "---";
                    const userPone = row.user?.phone || row.phone || "---";
                    const pairDisplay = getPairDisplay(row);

                    return (
                      <tr key={orderId}>
                        <td>
                          <span className="orderbook-order-id">{orderId}</span>
                        </td>
                        <td>
                          <span className="orderbook-order-id">{binanceOrderId}</span>
                        </td>
                        <td>{formatDate(row.createdAt)}</td>
                        <td>
                          {traderId ? (
                            <Link
                              to={`/dashboard/trader/${traderId}`}
                              state={{ trader: row.user }}
                              className="orderbook-user-link"
                              title="View trader details"
                            >
                              {userId}
                            </Link>
                          ) : (
                            userId || "---"
                          )}
                        </td>
                        <td>{userEmail}</td>
                        <td>{userPone}</td>
                        <td>
                          <strong>{pairDisplay}</strong>
                        </td>
                        <td>
                          <span className={`orderbook-side orderbook-side--${row.side?.toLowerCase()}`}>
                            {row.side}
                          </span>
                        </td>
                        <td>{row.order_type}</td>
                        <td>
                          {typeof row.price === "number"
                            ? row.price.toLocaleString("en-IN", { minimumFractionDigits: 2 })
                            : row.price}
                        </td>
                        <td>
                          {typeof row.quantity === "number"
                            ? row.quantity.toLocaleString("en-IN", { maximumFractionDigits: 4 })
                            : row.quantity}
                        </td>
                        <td>
                          {typeof row.filled === "number"
                            ? row.filled.toLocaleString("en-IN", { maximumFractionDigits: 4 })
                            : row.filled}
                        </td>
                        <td>
                          {typeof row.remaining === "number"
                            ? row.remaining.toLocaleString("en-IN", { maximumFractionDigits: 4 })
                            : row.remaining}
                        </td>
                        <td>{row.total_fee || 0}</td>
                        <td>
                          <span className={`orderbook-status orderbook-status--${(row.status || "").toLowerCase()}`}>
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {!loading && totalCount > 0 && (
            <div className="orderbook-pagination">
              <span className="orderbook-pagination__info">
                Showing {pageStart}–{pageEnd} of {totalCount}
                {totalPages > 1 && <> · Page {page} of {totalPages}</>}
              </span>
              {totalPages > 1 && (
                <div className="orderbook-pagination__buttons">
                  <button
                    type="button"
                    className="orderbook-pagination__btn"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <i className="fas fa-chevron-left" /> Prev
                  </button>
                  <button
                    type="button"
                    className="orderbook-pagination__btn"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next <i className="fas fa-chevron-right" />
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default OrderBookPage;
